import './leasing-accept-modal.scss';

import { useContext, useMemo } from 'react';

import type { TLeasingOrderResponse } from '@/components/App/Leasing/types';
import { CustomModal } from '@/components/Shared/Modal/CustomModal';
import { Button, CircledImage } from '@/components/Shared/ui';
import { formatPrice } from '@/services/formatter';
import { useAppSelector } from '@/store';

import { IeLeasingOrderContext } from '../../../IeOrder';
import { getAutoInfo } from '../../../helpers';

type TLeasingOfferInfo = {
  auto: string;
  balloonAmount: string;
  initialFee: string;
  leasingCompanyIcon: string;
  leasingCompanyName: string;
  loanAmount: string;
  monthlyPayment: string;
  rate: string;
  term: string;
};

type TLeasingAcceptModalProps = {
  focusedLeasingResponse: TLeasingOrderResponse | null;
  isAcceptLoading: boolean;
  onCancel: () => void;
  onSelectLeasingOffer: () => void;
  showAcceptModal: boolean;
};

const LeasingAcceptModal = ({
  focusedLeasingResponse,
  isAcceptLoading,
  onCancel,
  onSelectLeasingOffer,
  showAcceptModal
}: TLeasingAcceptModalProps) => {
  const { isActionsDisabled } = useContext(IeLeasingOrderContext);
  const { request } = useAppSelector((state) => state.ieLeasingOrder);

  const offerInfo: TLeasingOfferInfo | null = useMemo(() => {
    if (!focusedLeasingResponse || !request) return null;

    const {
      balloonAmount,
      initialPayment,
      leasingCompany,
      approvedAmount,
      firstMonthPayment,
      rate,
      loanPeriod
    } = focusedLeasingResponse;

    const autoInfo = getAutoInfo(request);

    return {
      auto: autoInfo.label,
      balloonAmount: balloonAmount !== null ? formatPrice(balloonAmount) : '-',
      initialFee: formatPrice(initialPayment || request.initialPayment),
      leasingCompanyIcon: leasingCompany.icon,
      leasingCompanyName: leasingCompany.name,
      loanAmount: approvedAmount !== null ? formatPrice(approvedAmount) : '-',
      monthlyPayment: formatPrice(firstMonthPayment || ''),
      rate: String(rate ?? ''),
      term: String(loanPeriod ?? '')
    };
  }, [focusedLeasingResponse, request]);

  return (
    <CustomModal
      isModalOpen={showAcceptModal}
      title="Предложение выбрано"
      handleCancel={onCancel}
      handleOk={onSelectLeasingOffer}
      editClass="ie-accept-offer-modal"
      footer={[
        <Button key="back" onClick={onCancel}>
          Отмена
        </Button>,
        <Button
          isLoading={isAcceptLoading}
          key="submit"
          variant="filled-red"
          onClick={onSelectLeasingOffer}
          disabled={isActionsDisabled}
        >
          Подтвердить
        </Button>
      ]}
    >
      <div>
        <p className="ie-accept-offer-modal__text">
          Нажимая “Подтвердить” клиент соглашается с условиями лизинговой
          компании
        </p>

        <div className="ie-accept-offer-modal__header">
          <CircledImage
            src={offerInfo?.leasingCompanyIcon || ''}
            alt={offerInfo?.leasingCompanyName || ''}
            size={40}
          />
          <div>
            <h6 className="ie-accept-offer-modal__chosen-text">
              Выбрана лизинговая компания
            </h6>
            <h5 className="ie-accept-offer-modal__bank">
              {offerInfo?.leasingCompanyName || ''}
            </h5>
          </div>
        </div>

        <div className="ie-accept-offer-modal__main-info">
          <div>
            <p className="ie-accept-offer-modal__main-label">
              Ежемесячный платеж
            </p>
            <p className="ie-accept-offer-modal__main-value">
              {offerInfo?.monthlyPayment || ''} ₸
            </p>
          </div>
          <div>
            <p className="ie-accept-offer-modal__main-label">Ставка</p>
            <p className="ie-accept-offer-modal__main-value ie-accept-offer-modal__main-value--red">
              {offerInfo?.rate || ''} %
            </p>
          </div>
        </div>

        <div className="ie-accept-offer-modal__sub-info">
          <div className="ie-accept-offer-modal__sub-item">
            <p className="ie-accept-offer-modal__sub-label">Сумма займа</p>
            <p className="ie-accept-offer-modal__sub-value">
              {offerInfo?.loanAmount || ''} ₸
            </p>
          </div>
          <div className="ie-accept-offer-modal__sub-item">
            <p className="ie-accept-offer-modal__sub-label">Авансовый взнос</p>
            <p className="ie-accept-offer-modal__sub-value">
              {offerInfo?.initialFee || ''} ₸
            </p>
          </div>
          <div className="ie-accept-offer-modal__sub-item">
            <p className="ie-accept-offer-modal__sub-label">
              Остаточная стоимость
            </p>
            <p className="ie-accept-offer-modal__sub-value">
              {offerInfo?.balloonAmount || ''} ₸
            </p>
          </div>
          <div className="ie-accept-offer-modal__sub-item">
            <p className="ie-accept-offer-modal__sub-label">Срок</p>
            <p className="ie-accept-offer-modal__sub-value">
              {offerInfo?.term || ''} месяца
            </p>
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

export default LeasingAcceptModal;
